import { useCallback, useRef, useState } from "react";

import useDebouncedCallback from "../../../hooks/useDebouncedCallback";
import { useCheckAiInterviewQuestionSubjectivityMutation } from "../../graphql";
import { useFeatureFlagForCurrentOrImpersonatedUser } from "../../graphql/hooks/useFeatureFlag";
import { useAiInterviewerContext } from "./AiInterviewerContext";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";
import {
  buildContentKey,
  COMPLIANCE_CHECK_DEBOUNCE_MS,
  getCriteria,
} from "./utils";

const useCheckSubjectivity = (
  questionIndex: number
): {
  checkSubjectivity: () => void;
  subjectivityWarning: boolean;
  loading: boolean;
} => {
  const aiInterviewer = useAiInterviewerContext();
  const { getValues } = useAiInterviewerFormContext();
  const [checkMutation, { loading }] =
    useCheckAiInterviewQuestionSubjectivityMutation();
  const [subjectivityWarning, setSubjectivityWarning] = useState(false);
  const lastCheckedContentRef = useRef<string>("");
  const lastCheckSucceededRef = useRef<boolean>(false);
  const lastWarningResultRef = useRef<boolean>(false);
  const featureEnabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "ai_interviewer:compliance_check"
  );

  const performCheck = useCallback(async () => {
    if (!featureEnabled) {
      return;
    }

    const formValues = getValues();
    const question = formValues.questions[questionIndex];

    if (!question?.question?.trim()) {
      setSubjectivityWarning(false);
      return;
    }

    // Get criteria based on question type
    const criteria = getCriteria(question);
    if (!criteria) {
      setSubjectivityWarning(false);
      return;
    }

    // Build content key from question, followups, and criteria
    const followups = question.followups?.filter(Boolean) || [];
    const contentKey = buildContentKey(
      question.question,
      followups as string[],
      criteria
    );

    // Skip if content hasn't changed and last check succeeded
    if (
      contentKey === lastCheckedContentRef.current &&
      lastCheckSucceededRef.current
    ) {
      setSubjectivityWarning(lastWarningResultRef.current);
      return;
    }

    try {
      const result = await checkMutation({
        variables: {
          aiInterviewerId: aiInterviewer.id,
          question: question.question,
          criteria,
        },
      });

      // Check if content changed while we were waiting - discard stale result
      const currentFormValues = getValues();
      const currentQuestion = currentFormValues.questions[questionIndex];
      if (!currentQuestion) return;

      const currentFollowups = currentQuestion.followups?.filter(Boolean) || [];
      const currentCriteria = getCriteria(currentQuestion);
      const currentContentKey = buildContentKey(
        currentQuestion.question || "",
        currentFollowups as string[],
        currentCriteria
      );

      if (currentContentKey !== contentKey) {
        return; // Content changed - discard stale result
      }

      // Update refs after successful check
      lastCheckedContentRef.current = contentKey;
      lastCheckSucceededRef.current = true;

      const isSubjective =
        result.data?.checkAiInterviewQuestionSubjectivity?.isSubjective ??
        false;
      lastWarningResultRef.current = isSubjective;
      setSubjectivityWarning(isSubjective);
    } catch {
      // Mark check as failed so it can be retried
      lastCheckSucceededRef.current = false;
      setSubjectivityWarning(false);
    }
  }, [
    aiInterviewer.id,
    getValues,
    questionIndex,
    checkMutation,
    featureEnabled,
  ]);

  const debouncedCheck = useDebouncedCallback(
    performCheck,
    COMPLIANCE_CHECK_DEBOUNCE_MS
  );

  // Clear warning immediately, then trigger debounced check
  // This prevents stale warnings from showing during the debounce window
  const checkSubjectivity = useCallback(() => {
    setSubjectivityWarning(false);
    debouncedCheck();
  }, [debouncedCheck]);

  return {
    checkSubjectivity,
    subjectivityWarning,
    loading,
  };
};

export default useCheckSubjectivity;
